/**
 * Public property detail — `GET properties/public/{id}/`. Full `PropertySerializer`
 * shape, same as the authenticated detail endpoint, but scoped to `APPROVED`
 * properties only and reachable with no session.
 */
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Bath,
  BedDouble,
  Building2,
  Calendar,
  DoorOpen,
  Layers,
  Maximize,
  Share2,
} from 'lucide-react';
import { parseApiError } from '../../lib/apiError';
import { getPublicProperty, listPublicProperties } from '../../api/endpoints/properties';
import { ROUTES } from '../../routes/paths';
import {
  deriveBathroomRange,
  deriveBedroomRange,
  deriveFromPrice,
  deriveTotalSquareFeet,
  deriveVacantPriceRange,
  formatBathroomRange,
  formatBedroomRange,
  formatKes,
  formatListedDate,
  formatPriceRange,
  isRecentlyListed,
} from '../../lib/propertyListing';
import { fetchNearbyAmenities, formatDistance } from '../../lib/nearbyAmenities';
import { PropertyPicture } from '../../components/PropertyPicture';
import { Lightbox } from '../../components/Lightbox';
import { PropertyMap } from '../../components/PropertyMap';

export function PublicPropertyDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const propertyId = Number(id);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [inquiryName, setInquiryName] = useState('');
  const [inquiryPhone, setInquiryPhone] = useState('');
  const [inquiryMessage, setInquiryMessage] = useState('');
  const [inquiryTerms, setInquiryTerms] = useState(false);
  const [inquiryError, setInquiryError] = useState<string | null>(null);
  const [inquiryStatus, setInquiryStatus] = useState<string | null>(null);
  const [shareStatus, setShareStatus] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['public-property', propertyId],
    queryFn: () => getPublicProperty(propertyId),
    enabled: Number.isFinite(propertyId),
  });

  // The two hooks below must run on EVERY render, regardless of query.isPending/
  // isError — they used to sit after the early returns below, so the first
  // render (pending) called fewer hooks than a later render (loaded), which
  // violates the Rules of Hooks and crashes with React error #310 ("Rendered
  // fewer hooks than expected"), unmounting the whole page (blank screen, no
  // visible error without an error boundary). `enabled` already deferred the
  // actual fetching correctly; only the hook *call* needed to be unconditional.
  const latitude = query.data?.latitude != null ? Number(query.data.latitude) : null;
  const longitude = query.data?.longitude != null ? Number(query.data.longitude) : null;
  const hasCoordinates =
    latitude !== null && longitude !== null && Number.isFinite(latitude) && Number.isFinite(longitude);

  const nearbyAmenities = useQuery({
    queryKey: ['public-property-amenities', propertyId, latitude, longitude],
    queryFn: () => fetchNearbyAmenities(latitude as number, longitude as number),
    enabled: hasCoordinates,
    staleTime: Infinity,
  });

  const propertyType = query.data?.property_type;
  const similarProperties = useQuery({
    queryKey: ['public-properties-similar', propertyType, propertyId],
    queryFn: () => listPublicProperties({ property_type: propertyType, page: 1 }),
    enabled: propertyType !== undefined,
  });

  if (!Number.isFinite(propertyId)) return <p>Invalid property id.</p>;
  if (query.isPending) return <p>Loading…</p>;
  if (query.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(query.error).message}
      </p>
    );
  }

  const property = query.data;
  const priceRange = deriveVacantPriceRange(property.units);
  const vacantUnits = property.units.filter((unit) => unit.is_vacant);
  const viewableImages = property.images
    .filter((image): image is typeof image & { image: string } => image.image !== null)
    .map((image) => ({
      src: image.image,
      alt: image.caption === '' ? `${property.name} photo` : image.caption,
    }));

  const bedroomRange = deriveBedroomRange(property.units);
  const bathroomRange = deriveBathroomRange(property.units);
  const totalSquareFeet = deriveTotalSquareFeet(property.units);
  const listedDate = formatListedDate(property.created_at);

  const similar = (similarProperties.data?.results ?? [])
    .filter((row) => row.id !== property.id)
    .slice(0, 3);

  /**
   * Matches the Flutter app: the inquiry never hits a server. It either hands
   * off to WhatsApp with a prefilled message, or — when the listing has no
   * WhatsApp number — just acknowledges inline.
   */
  function handleInquiry(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setInquiryError(null);
    setInquiryStatus(null);

    if (inquiryName.trim() === '') {
      setInquiryError('Please enter your name.');
      return;
    }
    if (!inquiryTerms) {
      setInquiryError('Please accept the terms and privacy policy to continue.');
      return;
    }

    if (property.whatsapp_number === '') {
      setInquiryStatus('Thank you! Your inquiry has been noted.');
      return;
    }

    const digits = property.whatsapp_number.replace(/[^\d+]/g, '');
    const text =
      `Hi, I'm ${inquiryName}.\nI'm interested in ${property.name} (${property.address}).\n` +
      (inquiryPhone !== '' ? `Reach me at: ${inquiryPhone}.\n` : '') +
      (inquiryMessage !== '' ? inquiryMessage : '');

    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(text)}`, '_blank', 'noreferrer');
  }

  /** Native share sheet on mobile browsers that support it; clipboard copy everywhere else. */
  async function handleShare(): Promise<void> {
    const url = window.location.href;
    if (navigator.share !== undefined) {
      try {
        await navigator.share({ title: property.name, url });
      } catch {
        // User cancelled the share sheet — not an error worth surfacing.
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareStatus('Link copied to clipboard.');
    } catch {
      setShareStatus(null);
    }
  }

  return (
    <section>
      <p>
        <Link to={ROUTES.publicProperties}>&larr; Back to listings</Link>
      </p>

      <div className="listing-header">
        <div className="listing-header__top">
          <div>
            <h1>{property.name}</h1>
            <p className="listing-header__type">{property.property_type}</p>
          </div>
          <div className="share-row">
            <button type="button" className="button button--outlined" onClick={() => void handleShare()}>
              <Share2 size={16} className="icon-inline" aria-hidden="true" />
              Share
            </button>
          </div>
        </div>
        {shareStatus !== null && <p role="status">{shareStatus}</p>}
        <p className="listing-header__address">
          {property.address}, {property.city}, {property.state} County
          {property.zip_code !== '' && ` ${property.zip_code}`}
        </p>
        <p className="badge-row">
          {property.vacant_units_count > 0 && (
            <span className="badge badge--available">Vacant units available</span>
          )}
          {isRecentlyListed(property.created_at) && <span className="badge badge--new">New</span>}
        </p>
        <p className="listing-header__price">
          {priceRange !== null ? formatPriceRange(priceRange) : 'Fully occupied'}
        </p>

        <ul className="spec-row">
          <li className="spec-row__item">
            <Building2 size={18} aria-hidden="true" />
            <span>{property.property_type}</span>
          </li>
          {bedroomRange !== null && (
            <li className="spec-row__item">
              <BedDouble size={18} aria-hidden="true" />
              <span>{formatBedroomRange(bedroomRange)}</span>
            </li>
          )}
          {bathroomRange !== null && (
            <li className="spec-row__item">
              <Bath size={18} aria-hidden="true" />
              <span>{formatBathroomRange(bathroomRange)}</span>
            </li>
          )}
          {totalSquareFeet !== null && (
            <li className="spec-row__item">
              <Maximize size={18} aria-hidden="true" />
              <span>{totalSquareFeet.toLocaleString('en-KE')} sq ft</span>
            </li>
          )}
          <li className="spec-row__item">
            <DoorOpen size={18} aria-hidden="true" />
            <span>
              {property.vacant_units_count} / {property.total_units} vacant
            </span>
          </li>
          {property.year_built !== null && (
            <li className="spec-row__item">
              <Calendar size={18} aria-hidden="true" />
              <span>Built {property.year_built}</span>
            </li>
          )}
          {property.total_units > 1 && (
            <li className="spec-row__item">
              <Layers size={18} aria-hidden="true" />
              <span>{property.total_units} units</span>
            </li>
          )}
        </ul>

        {listedDate !== null && <p className="listing-header__listed">Listed on {listedDate}</p>}
      </div>

      {property.images.length > 0 && (
        <ul className="gallery">
          {property.images.map((image) => (
            <li key={image.id} className="gallery__item">
              {image.is_primary && <span className="gallery__badge">Primary</span>}
              {image.image !== null && (
                <button
                  type="button"
                  className="gallery__trigger"
                  onClick={() =>
                    setLightboxIndex(viewableImages.findIndex((viewable) => viewable.src === image.image))
                  }
                >
                  <PropertyPicture
                    src={image.image}
                    alt={image.caption === '' ? `${property.name} photo` : image.caption}
                    size="medium"
                  />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <p>{property.description === '' ? 'No description provided.' : property.description}</p>

      {property.features.length > 0 && (
        <ul className="chip-row">
          {property.features.map((feature) => (
            <li key={feature} className="chip">
              {feature}
            </li>
          ))}
        </ul>
      )}

      <section className="detail-section">
        <h2>Location</h2>
        {hasCoordinates && latitude !== null && longitude !== null ? (
          <PropertyMap latitude={latitude} longitude={longitude} label={property.name} />
        ) : (
          <p>Location coordinates not available.</p>
        )}
      </section>

      {hasCoordinates && nearbyAmenities.data !== undefined && nearbyAmenities.data.length > 0 && (
        <section className="detail-section">
          <h2>Nearby</h2>
          <ul className="nearby-amenities">
            {nearbyAmenities.data.map((amenity) => (
              <li key={`${amenity.category}-${amenity.name}`} className="nearby-amenities__item">
                <span className="nearby-amenities__name">{amenity.name}</span>
                <span className="nearby-amenities__meta">
                  {amenity.categoryLabel} · {formatDistance(amenity.distanceMeters)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="detail-section">
        <h2>Available units</h2>
        {vacantUnits.length === 0 && <p>No vacant units.</p>}
        {vacantUnits.length > 0 && (
          <ul className="unit-grid">
            {vacantUnits.map((unit) => (
              <li key={unit.id} className="unit-card">
                <p className="unit-card__number">Unit {unit.unit_number}</p>
                <p className="unit-card__meta">
                  {unit.bedrooms} bd / {unit.bathrooms} ba
                </p>
                <p className="unit-card__price">{formatKes(Number(unit.rent_amount))}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="contact-card">
        <div>
          <span className="contact-card__label">Contact</span>
          <p className="contact-card__value">
            {property.agent_phone ?? (property.whatsapp_number !== ''
              ? property.whatsapp_number
              : 'Sign in to contact the agent.')}
          </p>
        </div>
        <div className="contact-card__actions">
          {/* The Call option supplements the inquiry form below rather than
              being replaced by it. */}
          {property.agent_phone !== null && (
            <a className="button button--primary" href={`tel:${property.agent_phone}`}>
              Call
            </a>
          )}
          {property.agent_phone === null && property.whatsapp_number === '' && (
            <Link className="button button--outlined" to={ROUTES.login}>
              Sign in
            </Link>
          )}
        </div>

        <form className="inquiry-form" onSubmit={handleInquiry} noValidate>
          <h2>Enquire about this property</h2>

          {inquiryError !== null && (
            <p role="alert" className="form__error">
              {inquiryError}
            </p>
          )}
          {inquiryStatus !== null && <p role="status">{inquiryStatus}</p>}

          <label htmlFor="inquiry-name">Your name</label>
          <input
            id="inquiry-name"
            required
            value={inquiryName}
            onChange={(event) => setInquiryName(event.target.value)}
          />

          <label htmlFor="inquiry-phone">Your phone (optional)</label>
          <input
            id="inquiry-phone"
            type="tel"
            value={inquiryPhone}
            onChange={(event) => setInquiryPhone(event.target.value)}
          />

          <label htmlFor="inquiry-message">Message (optional)</label>
          <textarea
            id="inquiry-message"
            value={inquiryMessage}
            onChange={(event) => setInquiryMessage(event.target.value)}
          />

          <label htmlFor="inquiry-terms">
            <input
              id="inquiry-terms"
              type="checkbox"
              checked={inquiryTerms}
              onChange={(event) => setInquiryTerms(event.target.checked)}
            />{' '}
            I accept the Terms of Service and Privacy Policy
          </label>

          <button type="submit">Send enquiry</button>
        </form>
      </div>

      {similar.length > 0 && (
        <section className="detail-section">
          <h2>Similar properties</h2>
          <ul className="similar-properties">
            {similar.map((row) => {
              const rowFromPrice = deriveFromPrice(row.units);
              return (
                <li key={row.id} className="property-card">
                  {row.primary_image?.image != null ? (
                    <PropertyPicture
                      className="property-card__thumb"
                      src={row.primary_image.image}
                      alt=""
                      size="thumb"
                    />
                  ) : (
                    <span
                      className="property-card__thumb thumb--placeholder"
                      aria-hidden="true"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      No photo
                    </span>
                  )}
                  <div className="property-card__body">
                    <h3>
                      <Link to={`${ROUTES.publicProperties}/${row.id}`}>{row.name}</Link>
                    </h3>
                    {rowFromPrice !== null ? (
                      <p className="listing-price">From {formatKes(rowFromPrice)}</p>
                    ) : (
                      <p className="listing-price listing-price--muted">Fully occupied</p>
                    )}
                    <p>
                      {row.property_type}, {row.city}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <Lightbox
        images={viewableImages}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={setLightboxIndex}
      />
    </section>
  );
}

export default PublicPropertyDetailPage;
